import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, CheckCircle, ThumbsUp, MessageSquare, Send, ChevronDown, ChevronUp, User, Globe, HelpCircle, Calendar, CheckSquare, EyeOff } from 'lucide-react';
import { Transition } from '@headlessui/react';
import Cookies from 'js-cookie';
import DOMPurify from 'dompurify';

const TEMP_HIDE_COOKIE_PREFIX = 'spotlight_temp_hide_';
const TEMP_HIDE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Type definitions
interface LeftContent {
  type: 'gradient' | 'image' | 'video' | 'fullScreenVideo' | 'confetti';
  content: string;
  alt?: string;
  showControls?: boolean;
  autoPlay?: boolean;
}

interface SurveyOption {
  value: string;
  label: string;
}

interface SurveyQuestion {
  id: string;
  question: string;
  type: string;
  options?: string[];
}

interface Survey {
  question?: string;
  options?: string[];
  questions?: SurveyQuestion[];
}

interface FormField {
  id: string;
  label: string;
  type: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  helpText?: string;
}

interface FormData {
  fields: FormField[];
}

interface CarouselStep {
  title: string;
  subtitle?: string;
  content: string;
}

interface Carousel {
  steps: CarouselStep[];
  currentStep: number;
  onNextStep: () => void;
  onPrevStep: () => void;
}

interface ExternalLink {
  text: string;
  url: string;
  icon?: React.ReactNode;
}

interface Agreement {
  text: string;
  required?: boolean;
  onAgree?: (agreed: boolean) => void;
}

interface ActionButton {
  text: string;
  onClick?: (data: { 
    surveyAnswer?: {
      questionId?: string;
      question?: string;
      answer?: string | undefined;
      questions?: SurveyQuestion[];
    } | undefined, 
    formData?: any, 
    isAgreed?: boolean 
  }) => void;
}

interface DismissButton {
  text: string;
  onClick?: (data: { 
    surveyAnswer?: {
      questionId?: string;
      question?: string;
      answer?: string | undefined;
      questions?: SurveyQuestion[];
    } | undefined, 
    formData?: any, 
    isAgreed?: boolean 
  }) => void;
}

interface Sender {
  name: string;
  avatar?: string;
  role?: string;
}

interface Comment {
  id?: string;
  text: string;
  author?: {
    name: string;
    avatar?: string;
  };
  timestamp: string;
  visibility: 'everyone' | 'sender';
}

interface Deadline {
  date: string;
  text?: string;
}

interface SurveyResponse {
  questionId?: string;
  question?: string;
  answer?: string | undefined;
  questions?: SurveyQuestion[];
}

export interface SpotlightProps {
  id?: string;
  isOpen: boolean;
  onClose: () => void;
  leftContent?: LeftContent;
  title?: string;
  subtitle?: string;
  description?: string;
  survey?: Survey | null;
  form?: FormData | null;
  actionButton?: ActionButton | null;
  dismissButton?: DismissButton;
  sender?: Sender | null;
  carousel?: Carousel | null;
  // Social interaction
  showSocialInteractions?: boolean;
  allowComments?: boolean;
  likes?: number;
  hasLiked?: boolean;
  onLike?: (liked: boolean) => void;
  comments?: Comment[];
  onAddComment?: (comment: Comment) => void;
  // Question feature
  allowQuestions?: boolean;
  onAskQuestion?: (question: { question: string, timestamp: string }) => void;
  // Agreement feature
  agreement?: Agreement | null;
  // External link
  externalLink?: ExternalLink | null;
  // Deadline
  deadline?: Deadline | null;
  // Required flag - if true, spotlight must be completed and can only be temporarily hidden
  required?: boolean;
  // Hide duration in milliseconds (default: 5 minutes)
  hideDuration?: number;
  // Show hide button flag - if true, a hide button is shown
  showHideButton?: boolean;
  // Custom hide button text
  hideButtonText?: string;
}

const Spotlight: React.FC<SpotlightProps> = ({
  id = `spotlight-${Date.now()}`, // Generate unique ID if not provided
  isOpen,
  onClose,
  leftContent = {
    type: 'gradient', // 'gradient', 'image', 'video', 'fullScreenVideo'
    content: 'linear-gradient(to right bottom, #4f46e5, #7c3aed)', // URL or gradient
    alt: 'Background visual'
  },
  title = '',
  subtitle = '',
  description = '',
  survey = null, // { question: '', options: [] }
  form = null, // { fields: [] }
  actionButton = null, // { text: '', onClick: () => {} }
  dismissButton = { text: 'Ik heb dit gelezen', onClick: () => {} },
  sender = null, // { name: '', avatar: '', role: '' }
  carousel = null, // { steps: [], currentStep: 0, onNextStep: () => {}, onPrevStep: () => {} }
  // Social interaction
  showSocialInteractions = false,
  allowComments = true, // Allow comments or only likes
  likes = 0,
  hasLiked = false,
  onLike = null,
  comments = [],
  onAddComment = null,
  // Question feature
  allowQuestions = false,
  onAskQuestion = null,
  // Agreement feature
  agreement = null, // { text: '', required: true, onAgree: () => {} }
  // External link
  externalLink = null, // { text: '', url: '', icon: null }
  // Deadline
  deadline = null, // { date: '2023-07-30', text: 'Reageer voor' }
  // Required flag - if true, spotlight must be completed and can only be temporarily hidden
  required = false,
  // Hide duration in milliseconds (default: 5 minutes)
  hideDuration = TEMP_HIDE_DURATION,
  // Show hide button flag - if true, a hide button is shown
  showHideButton = false,
  // Custom hide button text
  hideButtonText = 'Verbergen',
}) => {
  const [surveyAnswer, setSurveyAnswer] = useState<string | undefined>(undefined);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentVisibility, setCommentVisibility] = useState<'everyone' | 'sender'>('everyone');
  const [hasLikedState, setHasLikedState] = useState(hasLiked);
  const [question, setQuestion] = useState('');
  const [isAskingQuestion, setIsAskingQuestion] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showTempHideModal, setShowTempHideModal] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const hideButtonRef = useRef<HTMLButtonElement>(null);

  // Update hasLikedState when hasLiked prop changes
  useEffect(() => {
    setHasLikedState(hasLiked);
  }, [hasLiked]);

  // Check if spotlight should be temporarily hidden based on cookie timestamp
  useEffect(() => {
    if (id && isOpen) {
      const cookieName = `${TEMP_HIDE_COOKIE_PREFIX}${id}`;
      const tempHideUntil = Cookies.get(cookieName);

      if (tempHideUntil) {
        const hideUntilTimestamp = parseInt(tempHideUntil, 10);
        const currentTime = Date.now();

        // If current time is before the "hide until" time and not a required spotlight
        if (currentTime < hideUntilTimestamp && !required) {
          onClose();
        }
      }
    }
  }, [isOpen, id, onClose, required]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen) {
        // For the main spotlight: ESC key
        if (e.key === 'Escape' && !showTempHideModal) {
          e.preventDefault();
          setShowTempHideModal(true);
        }
        // For the temporary hide modal: S key for cancel, Enter for hide
        else if (showTempHideModal) {
          if (e.key.toLowerCase() === 's') {
            e.preventDefault();
            setShowTempHideModal(false);
            cancelButtonRef.current?.focus();
          } else if (e.key === 'Enter') {
            e.preventDefault();
            handleTemporaryHide();
            hideButtonRef.current?.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, showTempHideModal]);

  // Reset state when the spotlight opens/closes
  useEffect(() => {
    if (isOpen) {
      setSurveyAnswer(undefined);
      setFormData({});
      setHasInteracted(false);
      setShowComments(false);
      setNewComment('');
      setQuestion('');
      setIsAskingQuestion(false);
      setIsAgreed(false);
      setShowTempHideModal(false);
      setIsDismissing(false);
    }
  }, [isOpen]);

  // Handle temporary hide
  const handleTemporaryHide = useCallback(() => {
    if (id) {
      const cookieName = `${TEMP_HIDE_COOKIE_PREFIX}${id}`;
      // Calculate the timestamp for when to show again
      const showAgainTimestamp = Date.now() + hideDuration;
      // Store the timestamp in the cookie
      Cookies.set(cookieName, showAgainTimestamp.toString(), {
        expires: new Date(showAgainTimestamp)
      });
    }
    setShowTempHideModal(false);
    onClose();
  }, [id, onClose, hideDuration]);

  // Handle close button click
  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTempHideModal(true);
  };

  // Handle dismiss with animation sequence
  const handleDismiss = () => {
    // First set the dismissing state to animate the button
    setIsDismissing(true);

    // Prepare survey data to send back
    let surveyData: SurveyResponse | undefined = undefined;
    if (survey) {
      if (survey.questions && survey.questions.length > 0) {
        // For new format: include question ID and answer
        surveyData = {
          questionId: survey.questions[0].id,
          answer: surveyAnswer,
          questions: survey.questions
        };
      } else if (survey.question) {
        // For old format: just include the answer
        surveyData = {
          question: survey.question,
          answer: surveyAnswer
        };
      }
    }

    // After a short delay, call the actual onClose
    setTimeout(() => {
      if (dismissButton.onClick) {
        dismissButton.onClick({ 
          surveyAnswer: surveyData, 
          formData, 
          isAgreed 
        });
      }
      onClose();
    }, 150); // Short delay for button animation
  };

  // Render HTML content safely
  const renderHTML = (html: string) => {
    return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasInteracted(true);
  };

  const handleSurveySelect = (option: string) => {
    setSurveyAnswer(option);
    setHasInteracted(true);
  };

  const handleLike = () => {
    setHasLikedState(!hasLikedState);
    if (onLike) {
      onLike(!hasLikedState);
    }
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim() && onAddComment) {
      onAddComment({
        text: newComment,
        visibility: commentVisibility,
        timestamp: new Date().toISOString()
      });
      setNewComment('');
    }
  };

  const handleQuestionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (question.trim() && onAskQuestion) {
      onAskQuestion({
        question,
        timestamp: new Date().toISOString()
      });
      setQuestion('');
      setIsAskingQuestion(false);
    }
  };

  const handleAgreementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
    if (agreement && agreement.onAgree) {
      agreement.onAgree(e.target.checked);
    }
  };

  const formatDeadlineDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const isDeadlinePassed = (dateString: string) => {
    const deadlineDate = new Date(dateString);
    const today = new Date();
    return deadlineDate < today;
  };

  const renderLeftContent = () => {
    // Full screen video takes up the entire spotlight
    if (leftContent.type === 'fullScreenVideo') {
      return null;
    }

    switch (leftContent.type) {
      case 'image':
        return (
          <div className="h-full w-full overflow-hidden">
            <img
              src={leftContent.content}
              alt={leftContent.alt}
              className="h-full w-full object-cover"
            />
          </div>
        );
      case 'video':
        return (
          <div className="h-full w-full overflow-hidden">
            <video
              src={leftContent.content}
              autoPlay
              muted
              loop
              className="h-full w-full object-cover"
              controls={leftContent.showControls}
            />
          </div>
        );
      case 'confetti':
        return (
          <div className="h-full w-full relative overflow-hidden">
            <div
              className="h-full w-full absolute top-0 left-0"
              style={{ background: leftContent.content }}
            />
            <div className="h-full w-full absolute top-0 left-0" style={{
              backgroundImage: `url('/images/confetti.svg')`,
              backgroundSize: 'cover',
              opacity: 0.7
            }}/>
          </div>
        );
      case 'gradient':
      default:
        return (
          <div
            className="h-full w-full"
            style={{ background: leftContent.content }}
          />
        );
    }
  };

  const renderSurvey = () => {
    if (!survey) return null;

    // Handle the new format with questions array
    if (survey.questions && survey.questions.length > 0) {
      const firstQuestion = survey.questions[0];
      return (
        <div className="mt-6 space-y-4">
          <h3 className="text-lg font-medium text-gray-900">{firstQuestion.question}</h3>
          <div className="space-y-2">
            {firstQuestion.options && firstQuestion.options.length > 0 ? (
              // Render options if available
              firstQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleSurveySelect(option)}
                  className={`w-full p-3 rounded-lg border text-left transition-all duration-200 ${
                    surveyAnswer === option
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center ${
                      surveyAnswer === option ? 'bg-blue-500' : 'border border-gray-300'
                    }`}>
                      {surveyAnswer === option && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    <span>{option}</span>
                  </div>
                </button>
              ))
            ) : (
              // Handle boolean type questions
              firstQuestion.type === 'boolean' && (
                <>
                  <button
                    onClick={() => handleSurveySelect('true')}
                    className={`w-full p-3 rounded-lg border text-left transition-all duration-200 ${
                      surveyAnswer === 'true'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center ${
                        surveyAnswer === 'true' ? 'bg-blue-500' : 'border border-gray-300'
                      }`}>
                        {surveyAnswer === 'true' && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <span>Ja</span>
                    </div>
                  </button>
                  <button
                    onClick={() => handleSurveySelect('false')}
                    className={`w-full p-3 rounded-lg border text-left transition-all duration-200 ${
                      surveyAnswer === 'false'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center ${
                        surveyAnswer === 'false' ? 'bg-blue-500' : 'border border-gray-300'
                      }`}>
                        {surveyAnswer === 'false' && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                      <span>Nee</span>
                    </div>
                  </button>
                </>
              )
            )}
          </div>
        </div>
      );
    }

    // Handle the old format with single question
    if (survey.question) {
      return (
        <div className="mt-6 space-y-4">
          <h3 className="text-lg font-medium text-gray-900">{survey.question}</h3>
          <div className="space-y-2">
            {survey.options && survey.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleSurveySelect(option)}
                className={`w-full p-3 rounded-lg border text-left transition-all duration-200 ${
                  surveyAnswer === option
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center ${
                    surveyAnswer === option ? 'bg-blue-500' : 'border border-gray-300'
                  }`}>
                    {surveyAnswer === option && <CheckCircle className="w-4 h-4 text-white" />}
                  </div>
                  <span>{option}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderForm = () => {
    if (!form) return null;

    return (
      <div className="mt-6 space-y-4">
        {form.fields.map((field, index) => (
          <div key={index} className="space-y-1">
            <label htmlFor={field.id} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                id={field.id}
                name={field.id}
                rows={3}
                value={formData[field.id] || ''}
                onChange={(e) => handleFormChange(field.id, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={field.placeholder}
                required={field.required}
              />
            ) : field.type === 'select' ? (
              <select
                id={field.id}
                name={field.id}
                value={formData[field.id] || ''}
                onChange={(e) => handleFormChange(field.id, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required={field.required}
              >
                <option value="" disabled>
                  {field.placeholder}
                </option>
                {field.options?.map((option, i) => (
                  <option key={i} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={field.type}
                id={field.id}
                name={field.id}
                value={formData[field.id] || ''}
                onChange={(e) => handleFormChange(field.id, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={field.placeholder}
                required={field.required}
              />
            )}
            {field.helpText && (
              <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderCarousel = () => {
    if (!carousel) return null;

    const { steps, currentStep } = carousel;
    const currentStepData = steps[currentStep];

    return (
      <div className="space-y-4">
        {/* Progress indicator */}
        <div className="flex justify-center space-x-1 my-4">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentStep
                  ? 'w-8 bg-blue-500'
                  : index < currentStep
                    ? 'w-4 bg-blue-300'
                    : 'w-4 bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-xl font-medium text-gray-900">{currentStepData.title}</h3>
            {currentStepData.subtitle && (
              <p className="mt-1 text-base text-gray-600">{currentStepData.subtitle}</p>
            )}
            <div className="mt-4 text-gray-600">
              {typeof currentStepData.content === 'string'
                ? renderHTML(currentStepData.content)
                : currentStepData.content}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={carousel.onPrevStep}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              currentStep > 0
                ? 'text-gray-700 hover:bg-gray-100'
                : 'text-gray-400 cursor-not-allowed'
            }`}
            disabled={currentStep === 0}
          >
            Vorige
          </button>

          <button
            onClick={carousel.onNextStep}
            className="px-4 py-2 bg-blue-600 rounded-md text-sm font-medium text-white hover:bg-blue-700 transition"
          >
            {currentStep < steps.length - 1 ? 'Volgende' : 'Afronden'}
          </button>
        </div>
      </div>
    );
  };

  const renderSocialInteractions = () => {
    if (!showSocialInteractions) return null;

    return (
      <div className="mt-6 pt-6 border-t border-gray-200">
        {/* Like and comment buttons */}
        <div className="flex items-center space-x-6">
          <button
            onClick={handleLike}
            className={`flex items-center space-x-2 text-sm font-medium ${
              hasLikedState ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ThumbsUp className={`h-5 w-5 ${hasLikedState ? 'fill-current' : ''}`} />
            <span>{hasLikedState ? likes : likes} likes</span>
          </button>

          {allowComments && (
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <MessageSquare className="h-5 w-5" />
              <span>{comments.length} reacties</span>
              {showComments ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          )}

          {allowQuestions && !isAskingQuestion && (
            <button
              onClick={() => setIsAskingQuestion(true)}
              className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <HelpCircle className="h-5 w-5" />
              <span>Stel een vraag</span>
            </button>
          )}
        </div>

        {/* Question form */}
        {allowQuestions && isAskingQuestion && (
          <div className="mt-4">
            <form onSubmit={handleQuestionSubmit} className="space-y-3">
              <div className="flex items-center text-sm text-gray-600">
                <HelpCircle className="h-4 w-4 mr-2" />
                <span>Stel een vraag aan {sender?.name || 'de afzender'}</span>
              </div>

              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Typ je vraag hier..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={3}
              />

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsAskingQuestion(false)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  disabled={!question.trim()}
                  className="px-3 py-1 bg-blue-600 rounded-md text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Vraag versturen
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Comments section */}
        {allowComments && showComments && (
          <div className="mt-4 space-y-4">
            {/* Existing comments */}
            {comments.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto p-2">
                {comments.map((comment, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700">
                          {comment.author?.avatar ? (
                            <img src={comment.author.avatar} alt={comment.author.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <User className="w-4 h-4" />
                          )}
                        </div>
                        <span className="font-medium text-sm">{comment.author?.name || 'Anoniem'}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        {comment.visibility === 'everyone' ? (
                          <Globe className="w-3 h-3 mr-1" />
                        ) : (
                          <User className="w-3 h-3 mr-1" />
                        )}
                        <span>{new Date(comment.timestamp).toLocaleString('nl-NL', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-700">{comment.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">Geen reacties. Wees de eerste die reageert!</p>
            )}

            {/* New comment form */}
            <form onSubmit={handleCommentSubmit} className="mt-3">
              <div className="flex items-center space-x-2 mb-2">
                <label className="text-xs font-medium text-gray-700">Zichtbaarheid:</label>
                <select
                  value={commentVisibility}
                  onChange={(e) => setCommentVisibility(e.target.value as 'everyone' | 'sender')}
                  className="text-xs border-gray-300 rounded-md"
                >
                  <option value="everyone">Iedereen</option>
                  <option value="sender">Alleen verzender</option>
                </select>
              </div>

              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Schrijf een reactie..."
                  className="flex-grow px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <button
                  type="submit"
                  disabled={!newComment.trim()}
                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  <Send className="w-4 h-4 mr-1" />
                  Verzenden
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  };

  // Render sender information
  const renderSender = () => {
    if (!sender) return null;

    return (
      <div className="mb-6 flex items-center">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
          {sender.avatar ? (
            <img src={sender.avatar} alt={sender.name} className="w-10 h-10 rounded-full" />
          ) : (
            <User className="w-5 h-5 text-blue-700" />
          )}
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-900">{sender.name}</h4>
          {sender.role && <p className="text-xs text-gray-500">{sender.role}</p>}
        </div>
      </div>
    );
  };

  // Render external link
  const renderExternalLink = () => {
    if (!externalLink) return null;

    return (
      <div className="mt-6">
        <a
          href={externalLink.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {externalLink.icon && <span className="mr-2">{externalLink.icon}</span>}
          {externalLink.text}
        </a>
      </div>
    );
  };

  // Render deadline
  const renderDeadline = () => {
    if (!deadline) return null;

    const isPassed = isDeadlinePassed(deadline.date);

    return (
      <div className={`mt-4 flex items-center text-sm ${isPassed ? 'text-red-600' : 'text-amber-600'}`}>
        <Calendar className="h-4 w-4 mr-1.5" />
        <span>
          {deadline.text || 'Deadline'}: <strong>{formatDeadlineDate(deadline.date)}</strong>
          {isPassed && ' (verlopen)'}
        </span>
      </div>
    );
  };

  // Render agreement checkbox
  const renderAgreement = () => {
    if (!agreement) return null;

    return (
      <div className="mt-6">
        <label className="flex items-start">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              checked={isAgreed}
              onChange={handleAgreementChange}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              required={agreement.required}
            />
          </div>
          <span className="ml-2 text-sm text-gray-700">
            {agreement.text}
            {agreement.required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </label>
      </div>
    );
  };

  // Render temporary hide modal
  const renderTempHideModal = () => {
    if (!showTempHideModal) return null;

    // Calculate readable time from milliseconds
    const readableTime = () => {
      const minutes = Math.floor(hideDuration / 60000);
      if (minutes < 60) {
        return `${minutes} ${minutes === 1 ? 'minuut' : 'minuten'}`;
      } else if (minutes < 1440) { // Less than a day
        const hours = Math.floor(minutes / 60);
        return `${hours} ${hours === 1 ? 'uur' : 'uur'}`;
      } else {
        const days = Math.floor(minutes / 1440);
        return `${days} ${days === 1 ? 'dag' : 'dagen'}`;
      }
    };

    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowTempHideModal(false)}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full m-4 p-6 z-10">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {required ? 'Let op' : 'Tijdelijk verbergen?'}
            </h3>

            <p className="mb-6 text-gray-600">
              {required
                ? 'Deze melding is belangrijk en vereist uw aandacht. U kunt deze tijdelijk verbergen, maar deze zal later weer verschijnen.'
                : `Wilt u deze melding tijdelijk verbergen? Deze zal over ${readableTime()} weer zichtbaar worden.`}
            </p>

            <div className="flex justify-center space-x-3">
              <button
                ref={cancelButtonRef}
                onClick={() => setShowTempHideModal(false)}
                className="px-3 py-1.5 text-xs bg-gray-200 rounded text-gray-800 hover:bg-gray-300 transition"
              >
                <span className="hidden sm:inline">Annuleren</span> <span className="sm:hidden">Terug</span>
              </button>
              <button
                ref={hideButtonRef}
                onClick={handleTemporaryHide}
                className="px-3 py-1.5 text-xs bg-blue-600 rounded text-white hover:bg-blue-700 transition"
              >
                Tijdelijk verbergen <span className="text-blue-300 text-xs">(Enter)</span>
              </button>
            </div>

            {required && (
              <p className="mt-4 text-xs text-amber-600">
                Tip: Klik op "{dismissButton?.text || 'Ik heb dit gelezen'}" om deze melding volledig af te handelen.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" ref={containerRef}>
          {/* Backdrop with blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 backdrop-blur-sm bg-black/30"
            onClick={() => setShowTempHideModal(true)}
          />

          {/* Container */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
              className={`relative flex w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl ${
                leftContent.type === 'fullScreenVideo' ? 'aspect-video' : ''
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button - with escape hint */}
              <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
                <span className="text-xs text-gray-500 bg-white/80 px-2 py-1 rounded-md backdrop-blur-sm">
                  ESC
                </span>
                <button
                  onClick={handleCloseClick}
                  className="rounded-full bg-white/80 p-1 text-gray-500 backdrop-blur-sm transition-colors hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Tijdelijk verbergen"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Full-screen video */}
              {leftContent.type === 'fullScreenVideo' ? (
                <div className="w-full h-full">
                  <video
                    src={leftContent.content}
                    className="h-full w-full object-cover"
                    controls
                    autoPlay={leftContent.autoPlay}
                  />
                </div>
              ) : (
                <>
                  {/* Left column */}
                  <div className="hidden w-5/12 bg-gray-100 md:block">
                    {renderLeftContent()}
                  </div>

                  {/* Right column */}
                  <div className="w-full p-6 md:w-7/12 md:p-8 lg:p-10">
                    <div className="h-full">
                      {/* Sender information */}
                      {renderSender()}

                      {/* Deadline */}
                      {renderDeadline()}

                      {title && !carousel && (
                        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl">
                          {title}
                        </h2>
                      )}

                      {subtitle && !carousel && (
                        <p className="mt-2 text-lg font-medium text-gray-600">
                          {subtitle}
                        </p>
                      )}

                      {description && !carousel && (
                        <div className="mt-4 text-base text-gray-500">
                          {renderHTML(description)}
                        </div>
                      )}

                      {renderCarousel()}
                      {renderSurvey()}
                      {renderForm()}
                      {renderExternalLink()}
                      {renderAgreement()}
                      {renderSocialInteractions()}

                      <div className="mt-8 flex items-center justify-end space-x-4">
                        {/* Hide button - only show if showHideButton is true */}
                        {showHideButton && !carousel && (
                          <button
                            onClick={() => setShowTempHideModal(true)}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          >
                            <EyeOff className="mr-2 h-4 w-4" />
                            {hideButtonText}
                          </button>
                        )}

                        {dismissButton && !carousel && (
                          <Transition
                            show={!isDismissing}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-100"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <button
                              onClick={handleDismiss}
                              disabled={agreement?.required && !isAgreed}
                              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {dismissButton.text}
                            </button>
                          </Transition>
                        )}

                        {actionButton && !carousel && (
                          <button
                            onClick={() => {
                              // Prepare survey data to send back
                              let surveyData: SurveyResponse | undefined = undefined;
                              if (survey) {
                                if (survey.questions && survey.questions.length > 0) {
                                  // For new format: include question ID and answer
                                  surveyData = {
                                    questionId: survey.questions[0].id,
                                    answer: surveyAnswer,
                                    questions: survey.questions
                                  };
                                } else if (survey.question) {
                                  // For old format: just include the answer
                                  surveyData = {
                                    question: survey.question,
                                    answer: surveyAnswer
                                  };
                                }
                              }
                              
                              if (actionButton.onClick) {
                                actionButton.onClick({ 
                                  surveyAnswer: surveyData, 
                                  formData, 
                                  isAgreed 
                                });
                              }
                              setHasInteracted(true);
                            }}
                            disabled={agreement?.required && !isAgreed}
                            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionButton.text}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </div>

          {/* Temporary hide modal */}
          {renderTempHideModal()}
        </div>
      )}
    </AnimatePresence>
  );
};

export default Spotlight; 